import { Component, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { BaseState, AttachedFile, Media } from '@base/entities/BaseEntities';
import { BaseComponent } from '@base/component/BaseComponent';
import { FileBoxDialogComponent } from '../../../dialogs/filebox-dialog/fileboxdialog.component';
import { ServiceBagService } from '@base/component/ServiceBag';
import { SysGlobals } from '@core/framework/sysglobals';
import { RC } from '@core/framework/resources.service';
import { FileSizePipe } from '../../../directives/Pipes';
import { PhotoBoxDialogComponent } from '@ui/dialogs/photobox-dialog/photoboxdialog.component';
import { ScreenService } from '@core/framework/screen.service';
import { Utils } from '@m4u-angular/core/helpers/Utils';

class State extends BaseState {
    Language: string;
    Files: AttachedFile[];
    DragOverCount = 0;
}

@Component({
    selector: 'm4u-attachedfiles',
    templateUrl: './attached-files.component.html',
    styleUrls: ['./attached-files.component.scss'],
    providers: [FileSizePipe]
})
export class AttachedFilesComponent extends BaseComponent<State> {
    @Input() set Files(value: AttachedFile[]) {
        this.State.Files = value;
        this.ExtendFileInfo();
    }

    @Input() HideSelectingFileOnMobile = false;
    @Input() AllowOnlyImages = false;
    @Input() AllowDownload = true;
    @Input() AllowUpload = true;
    @Input() Disabled: boolean;
    @Input() Accept: string;
    @Output() FilesChanged = new EventEmitter<AttachedFile[]>();
    @Output() DownloadItem = new EventEmitter<AttachedFile>();
    @Output() DeleteItem = new EventEmitter<AttachedFile>();
    @Input() MaxFileSize: number = 20 * 1024 * 1024;
    @Input() MaxFileSizeMessage = 'msgFileSizeRestriction';
    @Input() MaxFileSizeErrorMessage = 'errorMaxSizeUploadError';
    @Input() NotImageErrorErrorMessage = 'errorOnlyImageAllowed';
    @Input() AllowDragAndDrop: boolean;
    @Input() AllowMakePhoto: boolean;
    @Input() IsAutoSavePhotoAfterTake = false;
    @Input() IsShowExtension = true;

    @ViewChild(FileBoxDialogComponent) fileBoxDialog: FileBoxDialogComponent;
    @ViewChild(PhotoBoxDialogComponent) photoBoxDialog: PhotoBoxDialogComponent;

    constructor(A: ServiceBagService,
        public screen: ScreenService,
        private fileSizePipe: FileSizePipe) {
        super(A, 'AttachedFilesComponent', State);
    }

    override OnInitState() {
        this.State.Language = SysGlobals.Language;
        this.State.Files = this.State.Files || [];
    }

    OnDownloadItem(file: AttachedFile) {
        if (this.AllowDownload) {
            this.DownloadItem.emit(file);
        }
    }

    OnDeleteItem(file: AttachedFile) {
        const idx = this.State.Files.findIndex(f => f.Name === file.Name);
        if (idx !== -1) {
            this.State.Files.splice(idx, 1);
            this.DeleteItem.emit(file);
            this.FilesChanged.emit(this.State.Files);
        }
    }

    OnAddFilesToUpload() {
        this.fileBoxDialog.ShowDialog((list: File[]) => {
            if (list && list.length) {
                this.OnChangeUpload(list);
            }
        });
    }

    OnChangeUpload(files: File[]) {
        let maxSizeError = false;
        let notImageError = false;

        let _files = [];
        if (this.Accept) {
            const acceptFileExtensions = this.Accept.split(';').join(',').split(',').map(x => x.trim().toLowerCase().split('.').pop());
            for (const file of files) {
                const fileExtension = file.name.trim().toLowerCase().split('.').pop();
                if (acceptFileExtensions.find(a => a === fileExtension) != null) _files.push(file);
            }
        } else {
            _files = files;
        }

        for (const file of _files) {
            const newFiles = this.State.Files.filter(f => !!f.NewFile);
            if (newFiles.findIndex(fu => fu.NewFile.name === file.name) === -1) {

                if (this.AllowOnlyImages && !this.IsImage(file.type)) {
                    notImageError = true;
                } else if (file.size > this.MaxFileSize) {
                    maxSizeError = true;
                } else {
                    const newFile = {
                        Name: file.name,
                        NewFile: file,
                        Size: file.size,
                        IconStyle: 'mdi mdi-file-outline',
                        IsDisplayedAsImage: this.IsImage(file.type)
                    } as AttachedFile;
                    this.State.Files.push(newFile);
                }
            }
        }

        if (notImageError) this.UI.StopMessage(RC(this.NotImageErrorErrorMessage));
        if (maxSizeError) this.UI.StopMessage(RC(this.MaxFileSizeErrorMessage, this.fileSizePipe.transform(this.MaxFileSize)));

        this.FilesChanged.emit(this.State.Files);
    }

    OnDropFiles(e) {
        this.PreventDefault(e);
        this.State.DragOverCount--;
        this.OnChangeUpload(e.dataTransfer.files);
    }

    OnDragLeave(e) {
        this.PreventDefault(e);
        this.State.DragOverCount--;
    }

    OnDragEnter(e) {
        this.PreventDefault(e);
        this.State.DragOverCount++;
    }

    OnDragOver(e) {
        this.PreventDefault(e);
    }

    IsDragOver(): boolean {
        return this.State.DragOverCount > 0 ? true : false;
    }

    OnShowPhotoBox() {
        this.photoBoxDialog.ShowDialogShort((photos: Media[]) => {
            if (photos) {
                let photoNumber = 0;
                this.OnChangeUpload(photos.map((photo) => this.ConvertBase64ToImageJpeg(photo.ImageBase64, photoNumber++)));
            }
        });
    }

    private IsImage(fileType: string): boolean {
        return fileType.startsWith('image');
    }

    private ExtendFileInfo() {
        if (this.State.Files) {
            this.State.Files.forEach(f => {
                f.IconStyle = this.GetIconStyle(f);
            });
        }
    }

    private GetIconStyle(file: AttachedFile): string {
        return Utils.GetIconStyle(file.Extension);
    }

    private PreventDefault(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    private ConvertBase64ToImageJpeg(base64: string, imageNumber: number) {
        const photoInfo = base64.split(';');
        const decodedData = window.atob(photoInfo[1].substring(photoInfo[1].indexOf(',') + 1));
        let dataLength = decodedData.length;
        const u8arr = new Uint8Array(dataLength);

        while (dataLength--) {
            u8arr[dataLength] = decodedData.charCodeAt(dataLength);
        }

        const name = `Photo_${this.GetStringDateNow()}.jpg`;

        return new File([u8arr], name, { type: 'image/jpeg' });
    }

    private GetStringDateNow() {
        const now = new Date();

        // eslint-disable-next-line max-len
        return `${this.FormatDatePart(now.getDate())}${this.FormatDatePart(now.getMonth() + 1)}${now.getFullYear()} _${this.FormatDatePart(now.getHours())}${this.FormatDatePart(now.getMinutes())}${this.FormatDatePart(now.getSeconds())}`;
    }

    private FormatDatePart(part: number) {
        return part.toString().padStart(2, '0');
    }
}
